import React from "react";

import cx from "classnames";

import { coalesce } from "@kazoohr/helpers";

import { Icon } from "../Icon";
import { Text } from "../Text";
import { UtilityText } from "../UtilityText";

import styles from "./WizardVertical.scss";

export type WizardVerticalStatus = "COMPLETED" | "CURRENT" | "FUTURE" | "ERROR";

export interface StepProps {
  /** One of the string literals in WIZARD_VERTICAL_STATUSES */
  status: WizardVerticalStatus;
  /** Text that represents the name of the step */
  title: string;
  /** Text that will display beneath the title as additional instructions */
  utilityText?: string;
  /** URL that will be passed to linkComponent */
  url?: string;
  /** Whether the step is disabled */
  disabled?: boolean;
  /** Called every time the step is clicked.
   * For it to work, the step must be enabled, and `useButtons` should be true. */
  onClick?: () => void;
}

interface WizardVerticalWithoutButton {
  /** If true, the wizard will use a button for each step, instead of a link */
  useButtons?: false;
  /** Component that wraps each step in a link; this will be Link in Kazoo Web */
  linkComponent: React.ComponentType<any>;
}

interface WizardVerticalWithButton {
  /** If true, the wizard will use a button for each step, instead of a link */
  useButtons: true;
  /** Component that wraps each step in a link; this will be Link in Kazoo Web */
  linkComponent?: never;
}

interface WizardVerticalSharedProps {
  /** Array of steps used to render each item in the wizard */
  steps: StepProps[];
  /** Used for QA testing */
  dataTestId?: string;
}

export type WizardVerticalProps = WizardVerticalSharedProps &
  (WizardVerticalWithoutButton | WizardVerticalWithButton);

interface WizardIconProps {
  status: WizardVerticalStatus;
  isLastIcon: boolean;
}

export function WizardVertical({
  steps,
  useButtons = false,
  linkComponent,
  dataTestId,
}: WizardVerticalProps): JSX.Element {
  const StepWrapper = coalesce(useButtons ? "button" : linkComponent, "a");
  return (
    <section className={styles.wizard} data-testid={dataTestId}>
      {steps.map(
        ({ status, title, utilityText, url, disabled, onClick }, index) => {
          const dataTestIdIfCurrent =
            status === "CURRENT" ? `${dataTestId}-current` : "";
          const isLastIcon = steps.length === index + 1;
          const titleTextClassName = buildTitleTextClassNames(status);
          const utilityTextClassName = buildUtilityClassName(status);

          const StepLinkComponent = disabled ? "span" : StepWrapper;

          const additionalStepProps: Record<string, any> = {};
          if (!disabled && !useButtons) {
            additionalStepProps.to = url;
          }
          if (StepLinkComponent === "button") {
            additionalStepProps.type = "button";
          }

          const Component = StepLinkComponent;

          return (
            <Component
              onClick={!disabled ? onClick : undefined}
              key={index}
              className={cx({
                [styles.disabled]: disabled,
                [styles.button]: StepLinkComponent === "button",
              })}
              {...additionalStepProps}
            >
              <div
                className={styles.wizardStep}
                data-testid={dataTestIdIfCurrent}
              >
                <WizardIcon status={status} isLastIcon={isLastIcon} />
                <div className={styles.textWrapper}>
                  <Text size="small" paragraph>
                    <span className={titleTextClassName}>{title}</span>
                  </Text>
                  <UtilityText size="small">
                    <span className={utilityTextClassName}>{utilityText}</span>
                  </UtilityText>
                </div>
              </div>
            </Component>
          );
        }
      )}
    </section>
  );
}

function WizardIcon({ status, isLastIcon }: WizardIconProps): JSX.Element {
  const iconSectionLast = isLastIcon ? styles["iconSection--lastStep"] : "";
  const iconSectionClassNames = `${styles.iconSection} ${iconSectionLast}`;

  return (
    <div className={iconSectionClassNames}>
      <div className={buildIconWrapperClassNames(status)}>
        {(status === "COMPLETED" || status === "ERROR") && (
          <Icon
            color={styles.white}
            variant="regular"
            icon={status === "COMPLETED" ? "checkmark" : "warning_no_outline"}
          />
        )}
      </div>
    </div>
  );
}

function buildTitleTextClassNames(status: WizardVerticalStatus): string {
  const regular = styles.titleText + " ";
  const warning = status === "ERROR" ? styles["titleText--warning"] : "";
  return regular + warning;
}

function buildUtilityClassName(status: WizardVerticalStatus): string {
  return (
    styles.utilityText +
    " " +
    (status === "ERROR" ? styles["utilityText--warning"] : "")
  );
}

function buildIconWrapperClassNames(status: WizardVerticalStatus): string {
  switch (status) {
    case "CURRENT":
      return currentIconWrapperClassName;
    case "FUTURE":
      return futureIconClassName;
    case "ERROR":
      return errorIconClassName;
    case "COMPLETED":
    default:
      return completedIconWrapperClassName;
  }
}

const iconWrapperClassName = styles["iconSection__iconWrapper"];
const completedIconWrapperClassName = iconWrapperClassName;
const currentIconWrapperClassName =
  iconWrapperClassName + " " + styles["iconSection__iconWrapper--current"];
const futureIconClassName =
  iconWrapperClassName + " " + styles["iconSection__iconWrapper--future"];
const errorIconClassName =
  iconWrapperClassName + " " + styles["iconSection__iconWrapper--error"];
